import React, { useEffect, useState } from 'react';
import './SemanticSearch.css';
import {
  searchAugment,
  searchIntent,
  searchFilterCategorizer,
  trendingQueries,
} from './services/apiService';
import IconFilter from './IconFilter';
import IconAiSparkles from './IconAiSparkles';

const SEMANTIC_FILTER_ORDER = ['genre', 'subgenre', 'thematic'];
const SEMANTIC_FILTER_KEY_MAP = {
  genre: 'genreCategory',
  subgenre: 'subgenreCategories',
  thematic: 'thematicCategories',
};

const getSortOrderInfoText = (sortOrder) => {
  switch (sortOrder) {
    case 'newest':
      return 'Die Treffer sind nach Erscheinungsdatum sortiert (neueste zuerst).';
    case 'oldest':
      return 'Die Treffer sind nach Erscheinungsdatum sortiert (älteste zuerst).';
    case 'relevance':
      return 'Die Treffer sind nach inhaltlicher Relevanz sortiert.';
    default:
      return null;
  }
};

export function getSemanticInfo(
  mappedOriginalQuery,
  mappedQuery,
  wasReformulated,
  keywordWeightPercent,
  semanticWeightPercent,
  threshold,
  sortOrder
) {
  const sortOrderInfoText = getSortOrderInfoText(sortOrder);

  return (
    <>
      {wasReformulated && (
        <div className="semantic-info-reformulation">
          Die KI-Suche hat {mappedOriginalQuery || '-'} in {mappedQuery || '-'} umformuliert.
        </div>
      )}
      <div className={wasReformulated ? 'semantic-info-parameters-spaced' : 'semantic-info-parameters'}>
        Die hybride Suche wurde mit einer Gewichtung von {keywordWeightPercent}% Keyword und {semanticWeightPercent}% Semantik und einem similarity threshold von {threshold} ausgefuehrt.
      </div>
      {sortOrderInfoText && (
        <div className="semantic-info-parameters">
          {sortOrderInfoText}
        </div>
      )}
    </>
  );
}

export function createSemanticSearchContext(value, correctQuery = true) {
  return {
    queryOverride: value.trim(),
    correctQuery,
  };
}

export function useSemanticSearchState({ config, isSemanticMode, searchContext }) {
  const [intentSuggestions, setIntentSuggestions] = useState([]);
  const [trendingSuggestions, setTrendingSuggestions] = useState([]);
  const [semanticFilters, setSemanticFilters] = useState([]);
  const [selectedSemanticFilters, setSelectedSemanticFilters] = useState({});

  useEffect(() => {
    if (!isSemanticMode) {
      return;
    }

    const loadTrending = async () => {
      try {
        const suggestions = await loadSemanticTrendingSuggestions(config);
        setTrendingSuggestions(suggestions);
      } catch (err) {
        console.error('Failed to fetch trending queries: ', err);
      }
    };

    loadTrending();
  }, [config, isSemanticMode]);

  const resetSemanticState = () => {
    setIntentSuggestions([]);
    setSemanticFilters([]);
    setSelectedSemanticFilters({});
  };

  const loadSemanticRefinementFeatures = async (usedQuery, ctxOverride = null) => {
    const ctx = ctxOverride || searchContext;

    try {
      const intentData = await loadSemanticIntentSuggestions(config, ctx);
      setIntentSuggestions(intentData);
    } catch (err) {
      console.error('Failed to fetch semantic intent suggestions: ', err);
      setIntentSuggestions([]);
    }

    try {
      const normalizedFilters = await loadSemanticFilters(config, usedQuery);
      setSemanticFilters(normalizedFilters);
      setSelectedSemanticFilters((prev) => reconcileSelectedSemanticFilters(prev, normalizedFilters));
    } catch (err) {
      console.error('Failed to fetch semantic filters: ', err);
      setSemanticFilters([]);
      setSelectedSemanticFilters({});
    }
  };

  return {
    intentSuggestions,
    trendingSuggestions,
    semanticFilters,
    selectedSemanticFilters,
    setSelectedSemanticFilters,
    resetSemanticState,
    loadSemanticRefinementFeatures,
  };
}

export async function loadSemanticTrendingSuggestions(config) {
  const data = await trendingQueries(config);
  const suggestions = Array.isArray(data) ? data : (data?.queries ?? []);
  return suggestions.filter((suggestion) => typeof suggestion === 'string' && suggestion.trim());
}

export async function loadSemanticIntentSuggestions(config, searchContext) {
  return searchIntent(config, searchContext);
}

export function getCategorizerResults(response) {
  if (!response) {
    return [];
  }

  const categories = Array.isArray(response)
    ? response.find((entry) => entry && typeof entry === 'object' && !Array.isArray(entry))
    : response?.output || response;

  if (!categories) {
    return [];
  }

  return SEMANTIC_FILTER_ORDER.reduce((acc, key) => {
    const matches = Array.isArray(categories[key]) ? categories[key] : [];
    const firstMatch = matches[0];
    const mappedKey = SEMANTIC_FILTER_KEY_MAP[key] || key;
    const filterFieldKey = `${mappedKey}.title.keyword`;

    if (!firstMatch?.title) {
      return acc;
    }

    acc.push({
      key: filterFieldKey,
      title: firstMatch.title,
      value: firstMatch.title,
      score: firstMatch.score,
    });

    return acc;
  }, []);
}

export async function loadSemanticFilters(config, queryText) {
  const data = await searchFilterCategorizer(config, queryText);
  return getCategorizerResults(data);
}

export function reconcileSelectedSemanticFilters(selectedSemanticFilters, normalizedFilters) {
  const selectedEntries = Object.entries(selectedSemanticFilters || {});

  if (selectedEntries.length === 0) {
    return selectedSemanticFilters;
  }

  return selectedEntries.reduce((acc, [categoryKey, filter]) => {
    const stillAvailable = normalizedFilters.some(
      (candidate) => candidate.key === filter.key && candidate.value === filter.value
    );

    if (stillAvailable) {
      acc[categoryKey] = filter;
    }

    return acc;
  }, {});
}

export function getNextSemanticSelectedFilters(selectedSemanticFilters, filter) {
  const selectedForCategory = selectedSemanticFilters[filter.key];
  const shouldClear = selectedForCategory?.value === filter.value;

  if (shouldClear) {
    return Object.keys(selectedSemanticFilters).reduce((acc, key) => {
      if (key !== filter.key) {
        acc[key] = selectedSemanticFilters[key];
      }

      return acc;
    }, {});
  }

  return {
    ...selectedSemanticFilters,
    [filter.key]: filter,
  };
}

export async function performSemanticSearch(
  config,
  requestConfig,
  keywordWeight,
  fromPos,
  searchContext,
  selectedSemanticFilters
) {
  const semanticFilter = Object.values(selectedSemanticFilters || {}).map((filter) => ({
    key: filter.key,
    value: filter.value,
  }));
  const data = await searchAugment(config, requestConfig, fromPos, searchContext, semanticFilter);
  const kw = Number(keywordWeight) || 0;
  const keywordWeightPercent = Math.round(kw * 100);
  const semanticWeightPercent = Math.round((1 - kw) * 100);
  const threshold = Number((requestConfig.sliderValue * 0.0001).toFixed(2));
  const returnedOriginalQuery = data?.output?.original_query || '';
  const returnedQuery = data?.output?.query || '';
  const sortOrder = data?.output?.sort_order || '';
  const wasReformulated = returnedOriginalQuery !== returnedQuery;

  return {
    data: data?.output?.documents || [],
    originalQuery: returnedOriginalQuery,
    usedQuery: returnedQuery,
    accordionInfo: getSemanticInfo(
      returnedOriginalQuery,
      returnedQuery,
      wasReformulated,
      keywordWeightPercent,
      semanticWeightPercent,
      threshold,
      sortOrder
    ) || null,
  };
}

export default function SemanticSearch({
  query,
  originalQuery,
  fromPosition,
  isMainSearchLoading,
  trendingSuggestions,
  intentSuggestions,
  semanticFilters,
  selectedSemanticFilters,
  onSuggestionClick,
  onSemanticFilterToggle,
  onNotCorrectedSearch,
  isLoadingResults,
}) {
  const showSearchCorrection = originalQuery !== query && fromPosition === 0;
  const showIntentSuggestions = (!isMainSearchLoading
    && intentSuggestions?.intent_suggestions
    && intentSuggestions.intent_suggestions.length > 0
    && fromPosition === 0);
  const showTrendingSuggestions = (!isMainSearchLoading
    && !query
    && !showIntentSuggestions
    && trendingSuggestions.length > 0
    && fromPosition === 0);
  const showSemanticFilters = (!isMainSearchLoading
    && query
    && semanticFilters.length > 0);
  const activeSuggestions = showIntentSuggestions
    ? intentSuggestions.intent_suggestions
    : (showTrendingSuggestions ? trendingSuggestions : []);
  const showSuggestionPillsRow = (!isMainSearchLoading
    && query
    && (activeSuggestions.length > 0 || showSemanticFilters));

  return (
    <>
      {!query && showTrendingSuggestions && (
        <div className="suggestion-container">
          <div className="suggestion-btn-row">
            {trendingSuggestions.map((suggestion, index) => (
              <button
                key={`pre-search-suggestion-${index}`}
                className="suggestion-btn suggestion-btn-suggestion"
                onClick={() => onSuggestionClick(suggestion)}
              >
                <span className="suggestion-btn-icon"><IconAiSparkles size={20} /></span>
                <span className="suggestion-btn-label">{suggestion}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      {showSuggestionPillsRow && (
        <div className="suggestion-container">
          <div className="suggestion-btn-row">
            {activeSuggestions.map((suggestion, index) => (
              <button
                key={`suggestion-${index}`}
                className="suggestion-btn suggestion-btn-suggestion"
                onClick={() => onSuggestionClick(suggestion)}
              >
                <span className="suggestion-btn-icon"><IconAiSparkles size={20} /></span>
                <span className="suggestion-btn-label">{suggestion}</span>
              </button>
            ))}
            {semanticFilters.map((filter) => (
              <button
                key={`${filter.key}-${filter.value}`}
                className={`suggestion-btn suggestion-btn-filter ${selectedSemanticFilters[filter.key]?.value === filter.value ? 'selected' : ''}`}
                onClick={() => onSemanticFilterToggle(filter)}
              >
                <span className="suggestion-btn-icon"><IconFilter size={18} /></span>
                <span className="suggestion-btn-label">{filter.title}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      {showSearchCorrection && (
        <div onClick={onNotCorrectedSearch} className="basic-search-query">
          Meintest Du doch: {originalQuery}?
        </div>
      )}
      {isLoadingResults && <div className='loading-container inline'><div className="spinner"></div></div>}
    </>
  );
}
